// =============================================================================
// discoveryLinks.ts — household-shared aggregator links (v0.2.26, v0.2.50)
// =============================================================================
//
// The Scholarships tab shows a collapsible card of scholarship-aggregator
// links. Every household starts with the defaults below, but the list is
// fully editable — any member can add or remove links, including removing
// the defaults.
//
// Storage: households/{hid}/discovery_links/{linkId}
//   { link_id, label, url, created_by, created_at }
//
// Seeding model:
//   - New households (v0.2.25 createHousehold) get every default written at
//     creation time; the household doc gets discovery_links_initialized: true
//     and discovery_links_version: DISCOVERY_LINKS_VERSION.
//   - Households created before v0.2.26 have no links and no flag.
//     ensureDiscoveryLinksInitialized() performs a ONE-TIME legacy seed: if
//     the flag is absent/false, it seeds the defaults and sets the flag. The
//     flag is what prevents deliberately-deleted links from reappearing —
//     once initialized, the original set is never re-seeded.
//   - v0.2.50 versioned backfill: when a later release ADDS a default (tagged
//     with the `added_in` version that introduced it), households whose
//     discovery_links_version is older get just the newer defaults appended,
//     skipping any whose site the household already has, and their version
//     is bumped IN THE SAME BATCH. A default the household deletes AFTER the
//     bump stays deleted, because the bump is what stops the backfill from
//     running again.
//
// Who runs it: ONLY a parent-role member, and only while the household is
// active. Both the legacy seed and the backfill write the household doc,
// which firestore.rules restricts to parents (and lapsed households are
// read-only), so an applicant-role load or a lapsed household would only
// attempt writes the rules deny — and a link-only write without the version
// bump would resurrect a deleted default on every load. Applicants simply
// see the new default after the next parent load.
//
// Default links use a deterministic doc id (defaultLinkId) so two parents
// loading the tab at the same moment write the same documents instead of
// creating duplicates.
// =============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

export interface DiscoveryLink {
  link_id: string;
  label: string;
  url: string;
  created_by: string | null;
  created_at: unknown;
}

export interface DefaultDiscoveryLink {
  label: string;
  url: string;
  /** The DISCOVERY_LINKS_VERSION that introduced this default. */
  added_in: number;
}

/**
 * The current default-link set version. Bump this whenever a default is
 * added to DEFAULT_DISCOVERY_LINKS, and tag the new entry with the same
 * number in `added_in`. Households below this version are backfilled with the
 * newer entries on their next Scholarships-tab load by a parent.
 *
 *   1 — v0.2.26: the five original parser-backed sites.
 *   2 — v0.2.50: + Scholarships.com.
 */
export const DISCOVERY_LINKS_VERSION = 2;

/** The default aggregator links seeded into every household. */
export const DEFAULT_DISCOVERY_LINKS: DefaultDiscoveryLink[] = [
  { label: 'BigFuture (College Board)', url: 'https://bigfuture.collegeboard.org/scholarships', added_in: 1 },
  { label: 'Bold.org', url: 'https://bold.org/scholarships/', added_in: 1 },
  { label: 'Scholly (Sallie Mae)', url: 'https://scholarships.salliemae.com/', added_in: 1 },
  { label: 'Foundation for the Carolinas', url: 'https://fftcscholarships.communityforce.com/', added_in: 1 },
  { label: 'NC Community Foundation', url: 'https://nccommunityfoundation.org/scholarships', added_in: 1 },
  // v0.2.50. The site's "Scholarship Search" buttons lead to a login-walled
  // app (app.scholarships.com); this directory page is the browsable listing
  // (live awards with amounts/deadlines + category links), so it's the link.
  { label: 'Scholarships.com', url: 'https://www.scholarships.com/financial-aid/college-scholarships/', added_in: 2 },
];

export function genLinkId(): string {
  return `link_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Hostname of a link with any leading "www." removed, lowercased. Returns
 * null for unparseable URLs.
 */
export function linkHostname(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * The key the backfill uses to decide whether a household already has a
 * site: the registrable domain (last two labels), so a manually added
 * "app.scholarships.com/…" counts as already having Scholarships.com.
 */
export function linkDomainKey(url: string): string | null {
  const host = linkHostname(url);
  if (!host) return null;
  const labels = host.split('.');
  return labels.length > 2 ? labels.slice(-2).join('.') : host;
}

/**
 * Deterministic doc id for a default link, derived from its hostname
 * (e.g. "link_default_scholarships-com"). Concurrent seeds/backfills by two
 * parents write the same document instead of creating duplicates.
 */
export function defaultLinkId(def: DefaultDiscoveryLink): string {
  const host = linkHostname(def.url) ?? def.label.toLowerCase();
  return `link_default_${host.replace(/[^a-z0-9]+/g, '-')}`;
}

function defaultLinkDoc(def: DefaultDiscoveryLink, createdBy: string, now: unknown) {
  return {
    link_id: defaultLinkId(def),
    label: def.label,
    url: def.url,
    created_by: createdBy,
    created_at: now,
  };
}

/**
 * Add every default link to a writeBatch for a household being created.
 * Used by createHousehold() (v0.2.25) so new households start pre-populated.
 * The caller is responsible for setting discovery_links_initialized: true and
 * discovery_links_version: DISCOVERY_LINKS_VERSION on the household doc in
 * the same (or an earlier) batch.
 */
export function seedDefaultLinksIntoBatch(
  fs: Firestore,
  batch: ReturnType<typeof writeBatch>,
  householdId: string,
  createdBy: string,
  now: unknown
): void {
  for (const def of DEFAULT_DISCOVERY_LINKS) {
    batch.set(doc(fs, 'households', householdId, 'discovery_links', defaultLinkId(def)), defaultLinkDoc(def, createdBy, now));
  }
}

/**
 * Make sure a household has its default links. Runs only for a parent-role
 * member of an active household (see the file header for why); for anyone
 * else it returns false without reading the links or writing anything.
 *
 * Step 1 — legacy one-time seed (households created before v0.2.26): if the
 * household doc's `discovery_links_initialized` flag is not true and the
 * household has no links, seed every default and set the flag + current
 * version in one batch. If links already exist, just set the flag and fall
 * through to step 2. Once the flag is true the original set is never
 * re-seeded — links a household has deliberately deleted stay deleted.
 *
 * Step 2 — versioned backfill (v0.2.50): if `discovery_links_version` is
 * older than DISCOVERY_LINKS_VERSION (absent = 1, the original set), append
 * the defaults introduced since then — skipping any whose domain the
 * household already has — and bump the version, all in ONE batch so links
 * and marker land together or not at all.
 *
 * Safe to call on every Scholarships-tab load; it's a getDoc-only no-op once
 * the household is current. Returns true if it wrote any links.
 */
export async function ensureDiscoveryLinksInitialized(
  fs: Firestore,
  householdId: string,
  userId: string,
  /** true only for a parent-role member — the only role allowed to update the household doc. */
  canWriteHousehold: boolean
): Promise<boolean> {
  if (!canWriteHousehold) return false;

  const householdRef = doc(fs, 'households', householdId);
  const snap = await getDoc(householdRef);
  if (!snap.exists()) return false;

  const data = snap.data();
  // Lapsed = read-only in the rules (householdActive()); don't attempt writes
  // that are guaranteed to be denied. The backfill runs after reactivation.
  if (data.billingStatus === 'lapsed') return false;

  const linksCol = collection(fs, 'households', householdId, 'discovery_links');
  const now = serverTimestamp();
  let existing = null as Awaited<ReturnType<typeof getDocs>> | null;

  // ---- Step 1: legacy one-time seed ----
  if (data.discovery_links_initialized !== true) {
    existing = await getDocs(linksCol);
    if (existing.empty) {
      // Seed every default + set the flag and version, in one batch.
      const batch = writeBatch(fs);
      seedDefaultLinksIntoBatch(fs, batch, householdId, userId, now);
      batch.update(householdRef, {
        discovery_links_initialized: true,
        discovery_links_version: DISCOVERY_LINKS_VERSION,
      });
      await batch.commit();
      return true;
    }
    // Links somehow already exist: don't double-seed the original set. The
    // flag is set in step 2's batch (or on its own if nothing newer is due).
  }

  // ---- Step 2: versioned backfill of defaults added after this household was seeded ----
  const version = typeof data.discovery_links_version === 'number' ? data.discovery_links_version : 1;
  const needsFlag = data.discovery_links_initialized !== true;
  if (version >= DISCOVERY_LINKS_VERSION && !needsFlag) return false;

  const newer = DEFAULT_DISCOVERY_LINKS.filter((d) => d.added_in > version);
  const batch = writeBatch(fs);
  let added = 0;
  if (newer.length > 0) {
    if (!existing) existing = await getDocs(linksCol);
    const presentDomains = new Set<string>();
    for (const d of existing.docs) {
      const key = linkDomainKey((d.data() as DiscoveryLink).url ?? '');
      if (key) presentDomains.add(key);
    }
    for (const def of newer) {
      const key = linkDomainKey(def.url);
      if (key && presentDomains.has(key)) continue; // household already has this site
      batch.set(doc(fs, 'households', householdId, 'discovery_links', defaultLinkId(def)), defaultLinkDoc(def, userId, now));
      added++;
    }
  }
  // Links + marker in the same batch: either both land or neither does.
  batch.update(householdRef, {
    discovery_links_initialized: true,
    discovery_links_version: DISCOVERY_LINKS_VERSION,
  });
  await batch.commit();
  return added > 0;
}

/**
 * Light URL validation/normalization for the "add link" form. Returns a
 * normalized URL string, or null if it doesn't look like a usable web URL.
 * Prepends https:// if the user omitted a scheme.
 */
export function normalizeLinkUrl(raw: string): string | null {
  let s = raw.trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) {
    s = 'https://' + s;
  }
  try {
    const u = new URL(s);
    // Require a dot in the hostname so "https://foo" is rejected.
    if (!u.hostname.includes('.')) return null;
    return u.toString();
  } catch {
    return null;
  }
}
